/* eslint-disable @typescript-eslint/no-unused-vars */
import { axiosInstances, getAxiosHeaders } from '@api/config/axios';
import { createTransaction, getTxnInfo } from '@api/form-verification';
import { FORM_URL_KEY } from '@global/constants/form';
import { navigateTo } from '@services/navigation';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { ServiceSelectionItem } from './services';

interface EvaluationPartner {
  name: string;
  displayName: string;
  price: number;
  currency: string;
  category: string[];
  image: string;
  form: {
    inputs: any[],
    pricing?: any
  };
  pricing?: any;
  serviceList: any[];
}

type PartnersSelectionHandle = {
  setupEvaluators: (services?: ServiceSelectionItem[]) => Promise<void>
};

interface PartnersSelectionProps {
  onBack: () => void;
  onContinue: (partner: EvaluationPartner, evaluationEnabled: boolean) => Promise<void>;
  orgId: string;
  txnId: string;
  formId: string;
  openEdit?: boolean;
  selectedServices: ServiceSelectionItem[];
  apiResponse?: any;
  services: any[];
  setEvaluatorCost: (cost: number) => void;
  selectedTranslationPartner: string;
  setSelectedTranslationPartner: (partner: string) => void;
  setFormData: (data: Record<string, any>) => void;
  shouldAllowEdit?: boolean;
  setEvaluationEnabled: (enabled: boolean) => void;
}

// Script to update options names with pricing information
const updateOptionsWithPricing = (evaluators: EvaluationPartner[]): EvaluationPartner[] => {
  return evaluators.map((evaluator) => {
    const updatedEvaluator = { ...evaluator };

    if (updatedEvaluator.form?.inputs && updatedEvaluator.pricing) {
      updatedEvaluator.form.inputs = updatedEvaluator?.form?.inputs?.map((input) => {
        if (input.fieldName === 'type' && input.options) {
          // Update evaluation type options with pricing
          input.options = input.options.map((option: any) => {
            const pricingInfo = updatedEvaluator.pricing?.find(
              (priceItem: any) => priceItem.name === option.value
            );
            const usdPrice = pricingInfo?.pricing?.find((p: any) => p.currency === 'USD')?.value;

            return {
              ...option,
              name: usdPrice ? `${option.name} - $${usdPrice}` : option.name
            };
          });
        }

        return input;
      });
    }

    return updatedEvaluator;
  });
};

// Separate function to fetch evaluators
const fetchEvaluators = async (selectedServices: string): Promise<EvaluationPartner[]> => {
  try {
    const response = await axiosInstances.verificationV1?.get(
      `/eval-configs?evaluationServiceType=${selectedServices}`,
      getAxiosHeaders()
    );
    if (response?.data.data) {
      const evaluatorsWithPricing = updateOptionsWithPricing(response?.data?.data || []);
      return evaluatorsWithPricing;
    }
    return [];
  } catch (error) {
    console.error('Failed to fetch evaluation partners:', error);
    return [];
  }
};

const PartnersSelection = forwardRef<PartnersSelectionHandle, PartnersSelectionProps>(
  (
    {
      // onBack,
      onContinue,
      orgId,
      txnId: initialTxnId,
      formId,
      openEdit = false,
      selectedServices = [],
      apiResponse,
      services,
      setEvaluatorCost,
      selectedTranslationPartner,
      setSelectedTranslationPartner,
      setFormData,
      setEvaluationEnabled
      // shouldAllowEdit = false
    },
    ref
  ) => {
    const [selectedPartner, setSelectedPartner] = useState<string>('');
    const [pricing, setPricing] = useState<Record<string, any>>({});

    // const [isLoading, setIsLoading] = useState<{ continue: boolean, fetch: boolean }>({
    //   continue: false,
    //   fetch: false
    // });
    const [evaluationPartners, setEvaluationPartners] = useState<EvaluationPartner[]>([]);
    const [txnId, setTxnId] = useState(initialTxnId);

    const getPricing = (partner: EvaluationPartner, translationService: boolean) => {
      const servicesAvailable = partner.serviceList;
      if (translationService) {
        return parseFloat(pricing['translation']);
      }
      const totalPrice = servicesAvailable.reduce((acc, service) => {
        if (service.value !== 'translation') {
          acc += parseFloat(pricing[service.value]);
        }
        return acc;
      }, 0);
      return totalPrice ?? 0;
    };

    useEffect(() => {
      const pricing = services.reduce((acc, service) => {
        acc[service.value] = service.pricing;
        return acc;
      }, {});
      setPricing(pricing);
    }, [services]);

    const init = async (servicesOverride?: ServiceSelectionItem[], apiRes = apiResponse) => {
      const effectiveServices = servicesOverride ?? selectedServices;
      try {
        let txnIdTemp = txnId || '';
        let evaluationEnabled = false;
        const loadEvaluationPartners = async () => {
          // setIsLoading((prev) => ({ ...prev, fetch: true, continue: true }));

          const evaluators = await fetchEvaluators(
            effectiveServices.map((s) => s.serviceType).join(',')
          );

          const evaluatorsWithPricing = evaluators.map((evaluator) => {
            return {
              ...evaluator
              // pricing: pricing[evaluator.name]
            };
          });
          setEvaluationPartners(evaluatorsWithPricing);
          evaluationEnabled = Boolean(
            effectiveServices.find((s) => s.serviceType === 'evaluation')
          );
          setEvaluationEnabled(evaluationEnabled);
          let selectedPartnerTemp = null;
          // Auto-select partner based on API response
          if (apiRes?.evaluationInfo?.length > 0) {
            let evaluationInfo = apiRes.evaluationInfo.findLast(
              (x: any) => x.serviceType !== 'translation'
            );
            if (!evaluationInfo) {
              evaluationInfo = apiRes.evaluationInfo.findLast((x: any) => x.serviceType);
            }
            const existingPartner = evaluators.find(
              (evaluator) => evaluator.name === evaluationInfo.provider
            );

            if (existingPartner) {
              selectedPartnerTemp = existingPartner.name;
              setSelectedPartner(existingPartner.name);
              setFormData({
                ...evaluationInfo,
                ...(evaluationInfo?.additionalData ?? {}),
                evaluationProvider: existingPartner.name
              });
            }
            const translationEvaluationInfo = apiRes.evaluationInfo.findLast(
              (x: any) => x.serviceType === 'translation'
            )?.provider;
            const existingTranslationPartner = evaluators.find(
              (evaluator) => evaluator.name === translationEvaluationInfo
            );

            //if opted for translation service and API has response for it, set the selected translation partner as well

            const translationPartner = evaluators.filter(
              (evaluator) => evaluator.name === 'trential'
            );
            if (translationPartner.length > 0) {
              handleTranslationPartnerSelect(translationPartner?.[0]?.name);
            }
            if (existingTranslationPartner) {
              setSelectedTranslationPartner(existingTranslationPartner.name);
            }
          } else if (evaluators.length > 0) {
            // Set first evaluator as default selected if no API response
            selectedPartnerTemp = evaluators?.[0]?.name;
            setSelectedPartner(evaluators?.[0]?.name);
          }

          return { evaluators, selectedPartnerTemp, evaluatorsWithPricing };
        };

        // eslint-disable-next-line prefer-const
        let { evaluators, selectedPartnerTemp, evaluatorsWithPricing } =
          await loadEvaluationPartners();
        if (!txnIdTemp) {
          const res = await createTransaction({ orgId, eKycFormId: formId });
          if (res?.data?.transactionId) {
            txnIdTemp = res?.data?.transactionId;
            setTxnId(res?.data?.transactionId);
          }
        }
        const res: any = await getTxnInfo(txnIdTemp, orgId);
        if (res?.code === 0) {
          if (res.data?.paymentDetails?.length > 0 && !openEdit) {
            return navigateTo(`/${FORM_URL_KEY}/${formId}/${txnIdTemp}?orgId=${orgId}`);
          }
          const evaluator = evaluators?.find(
            (evaluator) => evaluator.name === res?.data?.evaluationInfo[0]?.provider
          );

          if (evaluator) {
            selectedPartnerTemp = evaluator.name;
            setSelectedPartner(evaluator.name);
            const data = {
              ...(res?.data?.evaluationInfo[0].additionalData || {}),
              evaluationProvider: res?.data?.evaluationInfo[0].provider,
              type: res?.data?.evaluationInfo[0].type,
              evaluationServiceType: res?.data?.evaluationInfo[0].serviceType
            };
            if (data.txnId) {
              delete data.txnId;
              delete data.orgId;
            }
            if (data.dob) {
              data.dob = new Date(data.dob);
            }
          }
        }
        return { evaluationEnabled, selectedPartnerTemp, evaluatorsWithPricing };
      } catch (error) {
        console.error('Error loading evaluation partners:', error);
        setEvaluationPartners([]);
      } finally {
        // setIsLoading((prev) => ({ ...prev, fetch: false, continue: false }));
      }
    };

    // const handlePartnerSelect = (partnerId: string) => {
    //   const selectedService = selectedServices.find(
    //     (service) => service.serviceType !== 'translation'
    //   );
    //   if (selectedService?.verifierInit || !shouldAllowEdit) {
    //     showSnackbar('You cannot change this evaluator', 'error');
    //     return;
    //   }
    //   setSelectedPartner(partnerId);
    // };

    const handleTranslationPartnerSelect = (partnerId: string) => {
      setSelectedTranslationPartner(selectedTranslationPartner === partnerId ? '' : partnerId);
    };

    const handleContinue = async (
      evaluationEnabled: boolean,
      selectedPartnerTemp: string,
      evaluatorsWithPricing: any
    ) => {
      if (selectedPartnerTemp) {
        const selectedEvaluatorData = evaluatorsWithPricing.find(
          (partner: any) => partner.name === selectedPartnerTemp
        );
        const selectedTranslationEvaluatorData = evaluatorsWithPricing.find(
          (partner: any) => partner.name === selectedTranslationPartner
        );
        if (selectedEvaluatorData) {
          await onContinue(selectedEvaluatorData, evaluationEnabled);
          const translationCost = selectedTranslationEvaluatorData
            ? getPricing(selectedTranslationEvaluatorData, true)
            : 0;
          setEvaluatorCost(getPricing(selectedEvaluatorData, false) + translationCost);
        }
      }
    };

    useImperativeHandle(ref, () => {
      return {
        async setupEvaluators(services?: ServiceSelectionItem[], apiRes?: any) {
          await init(services, apiRes).then(
            ({ evaluationEnabled, selectedPartnerTemp, evaluatorsWithPricing }: any) => {
              handleContinue(
                evaluationEnabled as boolean,
                selectedPartnerTemp,
                evaluatorsWithPricing
              );
            }
          );
        }
      };
    }, [init, handleContinue]);

    return (
      // <>
      //   <Grid container>
      //     <Grid
      //       item
      //       xs={12}
      //       lg={12}
      //       sx={{
      //         display: 'flex',
      //         flexDirection: 'column',
      //         justifyContent: 'center',
      //         gap: '1rem',
      //         maxWidth: '600px',
      //         margin: '0 auto'
      //       }}>
      //       {/* Section Title */}
      //       <Typography
      //         variant="h6"
      //         sx={{
      //           fontWeight: 500,
      //           mb: 1,
      //           color: '#515151 !important',
      //           fontSize: '2.4rem'
      //         }}>
      //         Select the evaluation partner
      //       </Typography>

      //       {/* Show loading state */}
      //       {isLoading.fetch ? (
      //         <Box sx={{ margin: 0, width: '100%', gap: '10px' }}>
      //           <Skeleton
      //             variant="rectangular"
      //             sx={{
      //               width: '100%',
      //               height: '60px',
      //               marginBottom: '10px',
      //               borderRadius: '8px'
      //             }}
      //           />
      //           <Skeleton
      //             variant="rectangular"
      //             sx={{
      //               width: '100%',
      //               height: '60px',
      //               borderRadius: '8px',
      //               marginBottom: '10px'
      //             }}
      //           />
      //           <Skeleton
      //             variant="rectangular"
      //             sx={{
      //               width: '100%',
      //               height: '60px',
      //               borderRadius: '8px',
      //               marginBottom: '10px'
      //             }}
      //           />
      //         </Box>
      //       ) : (
      //         <>
      //           {/* NACES Members Section */}
      //           <Box sx={{ mb: 0 }}>
      //             <Typography
      //               variant="body2"
      //               sx={{
      //                 fontWeight: 500,
      //                 color: '#515151',
      //                 mb: 1,
      //                 fontSize: '1.6rem'
      //               }}>
      //               NACES members
      //             </Typography>

      //             {evaluationPartners
      //               .filter((partner) => partner.category.includes('NACES members'))
      //               .map((partner) => (
      //                 <Box key={partner.name} sx={{ mb: 1 }}>
      //                   <VerificationTile
      //                     label={partner.displayName || partner.name}
      //                     subtitle={`$${getPricing(partner, false)} onwards`}
      //                     iconImgUrl={partner.image}
      //                     disabled={false}
      //                     onClick={() => handlePartnerSelect(partner.name)}
      //                     iconWrapperColor="primary.light"
      //                     iconWrapperHeight="2.8rem"
      //                     sx={{
      //                       backgroundColor:
      //                         selectedPartner === partner.name ? '#E3F2FD' : 'bg.white',
      //                       border:
      //                         selectedPartner === partner.name
      //                           ? '2px solid #1C43C3'
      //                           : '1px solid #E0E0E0'
      //                     }}
      //                     rightAdorment={
      //                       <Radio
      //                         checked={selectedPartner === partner.name}
      //                         onChange={() => handlePartnerSelect(partner.name)}
      //                         sx={{
      //                           height: '1.7rem',
      //                           width: 'auto',
      //                           color: 'font.light',
      //                           '&.Mui-checked': {
      //                             color: '#1C43C3'
      //                           }
      //                         }}
      //                       />
      //                     }
      //                   />
      //                 </Box>
      //               ))}
      //           </Box>

      //           {/* General Evaluation Section */}
      //           <Box sx={{ mb: 2 }}>
      //             <Typography
      //               variant="body2"
      //               sx={{
      //                 fontWeight: 500,
      //                 color: '#666',
      //                 mb: 1,
      //                 fontSize: '1.6rem'
      //               }}>
      //               General Evaluation
      //             </Typography>

      //             {evaluationPartners
      //               .filter((partner) => partner.category.includes('General Evaluation'))
      //               .map((partner) => (
      //                 <Box key={partner.name} sx={{ mb: 1 }}>
      //                   <VerificationTile
      //                     label={partner.displayName || partner.name}
      //                     subtitle={`$${getPricing(partner, false)} onwards`}
      //                     iconImgUrl={partner.image}
      //                     disabled={false}
      //                     onClick={() => handlePartnerSelect(partner.name)}
      //                     iconWrapperColor="primary.light"
      //                     iconWrapperHeight="2.8rem"
      //                     sx={{
      //                       backgroundColor:
      //                         selectedPartner === partner.name ? '#E3F2FD' : 'bg.white',
      //                       border:
      //                         selectedPartner === partner.name
      //                           ? '2px solid #1C43C3'
      //                           : '1px solid #E0E0E0'
      //                     }}
      //                     rightAdorment={
      //                       <Radio
      //                         checked={selectedPartner === partner.name}
      //                         onChange={() => handlePartnerSelect(partner.name)}
      //                         sx={{
      //                           fontSize: '1.5rem',
      //                           width: 'auto',
      //                           color: 'font.light',
      //                           '&.Mui-checked': {
      //                             color: '#1C43C3'
      //                           }
      //                         }}
      //                       />
      //                     }
      //                   />
      //                 </Box>
      //               ))}
      //           </Box>

      //           {/* Translation Section */}
      //           {translationServiceSelected && (
      //             <Box sx={{ mb: 2 }}>
      //               <Typography
      //                 variant="body2"
      //                 sx={{
      //                   fontWeight: 500,
      //                   color: '#666',
      //                   mb: 1,
      //                   fontSize: '1.6rem'
      //                 }}>
      //                 Select Partner for translation
      //               </Typography>

      //               {evaluationPartners
      //                 .filter((partner) => partner.category.includes('Translation'))
      //                 .map((partner) => (
      //                   <Box key={partner.name} sx={{ mb: 1 }}>
      //                     <VerificationTile
      //                       label={partner.displayName || partner.name}
      //                       subtitle={`$${getPricing(partner, true)} onwards`}
      //                       iconImgUrl={partner.image}
      //                       disabled={false}
      //                       onClick={() => handleTranslationPartnerSelect(partner.name)}
      //                       iconWrapperColor="primary.light"
      //                       iconWrapperHeight="2.8rem"
      //                       sx={{
      //                         backgroundColor:
      //                           selectedTranslationPartner === partner.name
      //                             ? '#E3F2FD'
      //                             : 'bg.white',
      //                         border:
      //                           selectedTranslationPartner === partner.name
      //                             ? '2px solid #1C43C3'
      //                             : '1px solid #E0E0E0'
      //                       }}
      //                       rightAdorment={
      //                         <Radio
      //                           checked={selectedTranslationPartner === partner.name}
      //                           onChange={() => handleTranslationPartnerSelect(partner.name)}
      //                           sx={{
      //                             fontSize: '1.5rem',
      //                             width: 'auto',
      //                             color: 'font.light',
      //                             '&.Mui-checked': {
      //                               color: '#1C43C3'
      //                             }
      //                           }}
      //                         />
      //                       }
      //                     />
      //                   </Box>
      //                 ))}
      //             </Box>
      //           )}

      //           {/* No partners available message */}
      //           {evaluationPartners.length === 0 && (
      //             <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
      //               No evaluation partners available at the moment.
      //             </Typography>
      //           )}
      //         </>
      //       )}
      //     </Grid>
      //   </Grid>
      //   <Grid item xs={12} sx={{ flexBasis: 'unset', width: '100%' }}>
      //     {/* Continue Button */}
      //     <Box sx={{ mt: 3, mb: 2, display: 'flex', gap: '1rem' }}>
      //       <StandardButton
      //         sx={{ fontSize: '1.5rem', p: '1.6rem' }}
      //         invertColor
      //         variant="outlined"
      //         onClick={onBack}>
      //         Back
      //       </StandardButton>
      //       <StandardButton
      //         sx={{ fontSize: '1.5rem', p: '1.6rem' }}
      //         onClick={handleContinue}
      //         startIcon={
      //           isLoading?.continue && (
      //             <CircularProgress style={{ height: '1.5rem', width: '1.5rem', color: 'grey' }} />
      //           )
      //         }
      //         disabled={
      //           isLoading?.continue ||
      //           !selectedPartner ||
      //           (translationServiceSelected && !selectedTranslationPartner)
      //         }>
      //         Continue
      //       </StandardButton>
      //     </Box>
      //   </Grid>
      // </>
      <></>
    );
  }
);

PartnersSelection.displayName = 'PartnersSelection';

export default PartnersSelection;
